import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { StyledCountSection } from '@/components/common/ygen/sections/counter/counter.style';
import {Col, Container,Row} from "react-bootstrap";
import CountUp from 'react-countup';
import {gsap} from "gsap";
import useContainerOffset from '@/components/hooks/useContainerOffset';
import Button from '@/components/common/ui/buttons/Button';
import reactHtmlParser from 'react-html-parser';

const CountSectionCommon = ({subtitle, data, id, nolink}) => {
	const offset = useContainerOffset('.container');
	const containerRef = useRef(null);
	const containerCounterRef = useRef([]);
	const countUpStart = useRef(null);
	const startRefs = useRef([]);

	// Memoize the animation setup to prevent unnecessary re-renders
	const setupAnimations = useCallback(() => {
		const counterAnim = document.querySelectorAll('.text3 span');
		const $text3 = document.querySelector(".text3");
		const counterAnimBold = document.querySelectorAll('.text4 span');
		const $text4 = document.querySelector(".text4");

		// Cleanup previous animations
		gsap.killTweensOf([...counterAnim, ...counterAnimBold]);

		// Setup animations for text3
		counterAnim.forEach((el, index) => {
			gsap.fromTo(el, {
				autoAlpha: 0,
				y: 50 * (2 * index),
				ease: "none",
				duration: 1.3,
			}, {
				y: 0,
				autoAlpha: 1,
				ease: "power2",
				duration: 1.3,
				stagger: {
					id: `${index + 1}`,
					each: 0.5,
					repeatRefresh: true,
				},
				scrollTrigger: {
					id: `${index + 1}`,
					trigger: $text3,
					toggleActions: 'play none none reverse',
				}
			});
		});

		// Setup animations for text4
		counterAnimBold.forEach((el, index) => {
			gsap.fromTo(el, {
				autoAlpha: 0,
				y: 50 * (2 * index),
				ease: "none",
				duration: 1.3,
			}, {
				y: 0,
				autoAlpha: 1,
				ease: "power2",
				duration: 1.3,
				stagger: {
					id: `${index + 1}`,
					each: 1,
					repeatRefresh: true,
				},
				scrollTrigger: {
					id: `${index + 1}`,
					trigger: $text4,
					toggleActions: 'play none none reverse',
				}
			});
		});
	}, []);

	// Setup main counter animation
	useEffect(() => {
		setupAnimations();

		return () => {
			// Cleanup GSAP animations
			gsap.killTweensOf('.text3 span, .text4 span');
		};
	}, [setupAnimations]);

	// Setup intersection observer for main counter
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					countUpStart.current?.start();
				}
			},
			{ threshold: 0.5 }
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, []);

	// Setup intersection observers for individual counters
	useEffect(() => {
		const observers = [];

		containerCounterRef.current.forEach((ref, index) => {
			if (!ref) return;

			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) {
						startRefs.current[index]?.();
					}
				},
				{ threshold: 0.5 }
			);

			observer.observe(ref);
			observers.push(observer);
		});

		return () => observers.forEach((observer) => observer.disconnect());
	}, [data?.posts?.list]);

	// Memoize the main counter value
	const mainCounterValue = useMemo(() => ({
		start: data?.section_data?.subtitle ? data?.section_data?.subtitle - 200 : 10801,
		end: data?.section_data?.subtitle || 11000
	}), [data?.section_data?.subtitle]);

	// Memoize the counter items
	const counterItems = useMemo(() => (
		data?.posts?.list?.map((e, index) => ({
			start: e?.data?.subtitle ? e?.data?.subtitle - 10 : 10801,
			end: e?.data?.subtitle || 11000,
			title: e?.data?.title
		}))
	), [data?.posts?.list]);

	return (
		<StyledCountSection id={id || 'CountSectionCommon'} offset={offset} data-scroll-section className="counter">
			<Container fluid className="second_item">
				<img className="fixed_background" src="/images/static/counter.jpg" alt="Counter background" loading="lazy" />

				<Row className="counter_bottom">
					<Col md={12}>
						<div className="content_large">
							<h3 ref={containerRef}>
								<CountUp
									enableScrollSpy={true}
									start={mainCounterValue.start}
									end={mainCounterValue.end}
									duration={5}
									separator=","
									decimals={0}
									decimal=","
								>
									{({ countUpRef: countRef, start }) => {
										countUpStart.current = { start };
										return <span ref={countRef} />;
									}}
								</CountUp>
								<div className="plus">+</div>
							</h3>
							{data?.section_data?.short_desc && (
								<p className="fade-up">{reactHtmlParser(data?.section_data?.short_desc)}</p>
							)}
						</div>
					</Col>
				</Row>

				<Row className="counter_bottom">
					<Col md={{span: 3}}>
						<div className="content_last">
							{data?.section_data?.description && reactHtmlParser(data?.section_data?.description)}
							<Button
								background="#152637"
								margin="40px 0 0"
								color="#152637"
								pathColor="white"
								text="Let's Talk"
								src="/contact"
							/>
						</div>
					</Col>
					<Col md={{span: 8, offset: 1}}>
						<Row>
							{counterItems?.map((item, index) => (
								<Col className="counter_bottom_single" md={6} key={index}>
									<div
										className="counter"
										ref={(el) => {
											if (!containerCounterRef.current) containerCounterRef.current = [];
											containerCounterRef.current[index] = el;
										}}
									>
										<div className="count-text">
											<CountUp
												enableScrollSpy={true}
												start={item.start}
												end={item.end}
												duration={3}
												separator=","
												decimals={0}
												decimal=","
											>
												{({ countUpRef, start }) => {
													startRefs.current[index] = start;
													return <h4 className="text4" ref={countUpRef} />;
												}}
											</CountUp>
											+
										</div>
										<p className="fade-up">{item.title}</p>
									</div>
								</Col>
							))}
						</Row>
					</Col>
				</Row>
			</Container>
		</StyledCountSection>
	);
};


export default React.memo(CountSectionCommon);
